import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import isUndefined from 'lodash/isUndefined';
import {useForm, Controller} from 'react-hook-form';
import z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import config from '../../config/config';
import useError from '../../hooks/useError';
import FormInput from './FormInput';
import FormSectionHeading from './FormSectionHeading';

const inferSchema = fields => {
  const schemaShape = fields.reduce((acc, field) => {
    if (!field.name) return acc;
    let fieldSchema;
    switch (field.type) {
      case 'text':
        fieldSchema = z.string();
        break;
      case 'textarea':
        fieldSchema = z.string();
        break;
      case 'html':
        fieldSchema = z.string();
        break;
      case 'number':
        fieldSchema = z.union([z.number(), z.string().regex(/^\d+(\.\d+)?$/, 'Invalid number format').transform(Number)]);
        break;
      case 'currency':
        fieldSchema = z.number().min(0);
        break;
      case 'boolean':
        fieldSchema = z.boolean();
        break;
      case 'date':
        // can be a date OR a string
        fieldSchema = z.union([z.string(), z.date()]);
        break;
      case 'email':
        fieldSchema = z.email();
        break;
      case 'address':
        fieldSchema = z.object({
          address_line_1: z.string(),
          address_line_2: z.string().optional(),
          address_state: z.string(),
          address_city: z.string(),
          address_zip: z.string(),
          lat: z.number().optional(),
          lon: z.number().optional(),
          radius: z.number().optional(),
        });
        break;
      case 'daterange':
        fieldSchema = z.object({
          startDate: z.iso.datetime().nullable(),
          endDate: z.iso.datetime().nullable(),
        }).refine(data => {
          if (!data.endDate) return true;
          return new Date(data.startDate) <= new Date(data.endDate);
        }, 'End date must be after start date');
        break;
      case 'time':
        fieldSchema = z.string()
          .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9] (am|pm)$/i, 'Invalid time format');
        break;
      default:
        fieldSchema = z.any();
    }
    if (field?.schema) {
      fieldSchema = field.schema;
    } else if (!field?.required) {
      fieldSchema = fieldSchema.optional().nullable();
    }
    if (field?.validate) fieldSchema = fieldSchema.refine(field.validate, 'Invalid validation');
    acc[field.name] = fieldSchema;
    return acc;
  }, {});
  return z.object(schemaShape);
};

const submitHandler = ({
  handleSubmit, 
  onSubmit, 
  setError, 
  serializer,
  reset,
}) => async data => {
    // console.log('handleSubmit', data);
    try {
      await onSubmit(serializer(data));
      setError(null);
      reset(data);
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error);
    }
};

const handleValidationError = ({setError}) => errors => {
  console.log('handleValidationError', errors);
  const message = Object.entries(errors).map(([key, value]) => key + ': ' + value.message).join(', ');
  setError(new Error(message));
};

const sectionPrerequisitesMet = ({section, fields, values}) => {
  if (!section?.prerequisites) return true;
  return section.prerequisites.every(p => 
    !fields.some(f => 
      f.section === p 
      && f.required
      // check if value is valid
      && [undefined, null, ''].includes(values[f.name])
    )
  );
};

const Form = ({
  fields,
  schema,
  defaultValues = {},
  error = null,
  serializer = data => data, // default serializer is identity function
  sections = null, // optional form sections
  gridOptions = {rowSpacing: 0, columnSpacing: 1},
  sectionOptions = {textVariant: 'h2', style: {}},
  showSubmitButton = true,
  submitButtonText = 'Save',
  showSectionDivider = true,
  backgroundColor = '#ffffff',
  title = '',
  onSubmit = formData => console.log('form.submit.formValues', formData),
  onFormChange = null,
}) => {
  const {
    handleSubmit,
    setValue,
    control,
    formState,
    watch,
    reset,
  } = useForm({
    resolver: schema ? zodResolver(schema) : zodResolver(inferSchema(fields)),
    defaultValues,
  });
  const {create: setError} = useError();

  const values = watch();
  if (fields?.length === 0) return null;
  const _onSubmit = submitHandler({
    onSubmit, 
    setError, 
    serializer,
    reset,
  });
  const onValidationError = handleValidationError({setError});
  // console.log('formState', formState);
  const unsavedChanges = (
    Object.keys(formState.dirtyFields).length > 0
  );
  return (
    <Box style={{width: '100%', backgroundColor}}>
      <form onSubmit={handleSubmit(_onSubmit, onValidationError)}>
        <Grid container {...gridOptions}>
          {!!title &&
            <Grid item xs={12}>
              <Stack direction='row' alignItems='center' justifyContent='center'>
                <FormSectionHeading
                  title={title}
                  textVariant={sectionOptions?.textVariant || 'h2'}
                  style={sectionOptions?.style || {}}
                />
              </Stack>
            </Grid>
          }
          {!!error && 
            <Grid item xs={12}>
              <Alert severity="error">{error.message}</Alert>
            </Grid>
          }
          {(sections || [{}])
            .filter(s => 
              !s.prerequisites 
              || sectionPrerequisitesMet({section: s, fields, values})
            )
            .map((s, sectionIndex) => [
              !!s.label ? 
                <Grid item xs={12} key={(s.name || s.label) + 'heading'}>
                  <Box style={{paddingTop: showSectionDivider ? '1em' : '2em'}}>
                    {sectionIndex > 0 && showSectionDivider && 
                      <Divider style={{marginBottom: '2em'}} />
                    }
                    <FormSectionHeading
                      key={s.key || s?.componentKey || s.name + 'heading'}
                      title={s.label}
                      textVariant={sectionOptions?.textVariant || 'h2'}
                      style={sectionOptions?.style || {}}
                      subtitle={s.description}
                    />
                  </Box>
                </Grid>
              : null,
              ...(fields || [])
              .filter(f => sections ? f.section === s.name || (!f.section && !s.name) : true)
              .map((field, i) => ([
                field.prepend 
                ? <Grid item xs={12}>{field.prepend()}</Grid>
                : null,
                <Grid
                  key={field.key || field.name}
                  item 
                  xs={field.grid?.xs || 12}
                  md={field.grid?.md || 12}
                >
                  <Stack pt={0.5} pl={1} pr={1}>
                    <Controller
                      name={field.name}
                      control={control}
                      render={hookState => 
                        <FormInput
                          key={field.name}
                          controlled={true}
                          {...field}
                          {...hookState.field}
                          defaultValue={
                            defaultValues?.[field.name] !== undefined
                            ? defaultValues[field.name]
                            : (
                              field?.defaultValue !== undefined
                              ? field.defaultValue
                              :  undefined
                            )
                          }
                          helperText={hookState.fieldState?.error?.message || field.helperText}
                          error={!!hookState.fieldState?.error}
                          onChange={(e, value) => {
                            let _value = !isUndefined(value) ? value : e?.target?.value;
                            if (field.type === 'selectPatient') _value = e?.patientId;
                            // console.log('_value', e, value, _value);
                            if (field?.parser) {
                              const parsed = field.parser(e, _value);
                              Object.entries(parsed || {})
                                .filter(([key, val]) => key !== field.name)
                                .forEach(
                                  ([key, val]) => setValue(key, val, { shouldValidate: true, shouldDirty: true })
                                );
                                hookState.field.onChange(_value);
                            } else {
                              hookState.field.onChange(_value);
                            }
                            if (field?.onChange) field.onChange(e, _value);
                            if (onFormChange) onFormChange(e, {...values, [field.name]: _value});
                          }}
                        />
                      }
                    />
                  </Stack>
                </Grid>
            ]))
          ])}
          {showSubmitButton && 
            <Grid item xs={12}>
              <Stack direction='row' justifyContent='flex-end' spacing={2} pt={2}>
                <Button 
                  type='submit'
                  variant='contained'
                  disabled={formState.isSubmitting || !unsavedChanges}
                >
                  {
                    !unsavedChanges
                    ? <>
                      <CheckIcon 
                        fontSize="small" 
                        style={{color: config().colors.success}}
                        // color='success'
                      />
                      {' '}Saved
                    </>
                    : submitButtonText
                  }
                </Button>
              </Stack>
            </Grid>
          }
        </Grid>
      </form>
    </Box>
  );

};

Form.propTypes = {
  defaultFormData: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onFormChange: PropTypes.func,
  serializer: PropTypes.func, // optional function to serialize form data before passing to onSubmit
  getDefaultValues: PropTypes.func, // optional function to get default values for form fields
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  sections: PropTypes.arrayOf(PropTypes.shape(
    {
      key: PropTypes.string, // optional unique key for the section (if not provided, name will be used)
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      description: PropTypes.string,
    }
  )),
  fields: PropTypes.arrayOf(PropTypes.shape(
    {
      name: PropTypes.string.isRequired,
      section: PropTypes.string, // optional section name for grouping fields
      // form values
      label: PropTypes.string,
      type: PropTypes.string,
      defaultValue: PropTypes.any,
      placeholder: PropTypes.string,
      helperText: PropTypes.string,
      required: PropTypes.bool,
      validate: PropTypes.func, // optional validation function that takes field value and returns boolean
      // layout
      minWidth: PropTypes.number, // optional minimum width for the field in pixels
      fullWidth: PropTypes.bool, // whether the field should take up the full width of its container
      grid: PropTypes.shape({
        xs: PropTypes.number,
        sm: PropTypes.number,
        md: PropTypes.number,
        lg: PropTypes.number,
        xl: PropTypes.number,
      }),
      prepend: PropTypes.func, // optional function that returns a React element to render before the field
    }
  ))
};

export default Form;